import { Typography, Box, Card, CardContent } from "@mui/material";
// Components
import { getInstructionData, TOOL_TYPES } from "../../utils/aiToolkitInfo";
import theme from "../../theme";

export function InstructionSection({
  currentStep,
  completedSteps,
  toolType = TOOL_TYPES.ITERATOR,
  customInstructionData = null,
  title = "How it works",
}) {
  const instructionData = customInstructionData || getInstructionData(toolType);
  return (
    <Box sx={{ mb: 4, p: "16px", margin: "auto", maxWidth: "75rem" }}>
      <Typography
        variant="h6"
        sx={{
          textAlign: "left",
          fontWeight: 500,
          color: "text.primary",
          mb: 2,
        }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "1fr",
            md: "repeat(3, 1fr)",
          },
        }}
      >
        {instructionData.map((instruction) => {
          // See if this instruction step is completed, current or upcoming
          const isCompleted = completedSteps.has(instruction.id);
          const isCurrent = currentStep === instruction.id;

          return (
            <Card
              key={instruction.id}
              sx={{
                position: "relative",
                height: "100%",
                transition: "all 0.3s ease-out",
                border: "1px solid",
                borderColor: isCompleted
                  ? "divider"
                  : isCurrent
                  ? theme.palette.primary.brandSecondary
                  : "divider",
                opacity: isCompleted ? 0.5 : 1,
                "&:hover": {
                  transform: isCompleted ? "none" : "translateY(-4px)",
                  boxShadow: isCompleted ? "none" : 3,
                  borderColor: isCompleted
                    ? "divider"
                    : isCurrent
                    ? theme.palette.primary.brandSecondary
                    : theme.palette.primary.main,
                  "& .step-accent": {
                    backgroundColor: isCompleted
                      ? theme.palette.gray.medium
                      : isCurrent
                      ? theme.palette.primary.brandSecondary
                      : theme.palette.primary.main,
                  },
                },
              }}
            >
              <CardContent
                sx={{
                  p: 3,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      backgroundColor: isCompleted
                        ? theme.palette.gray.light
                        : isCurrent
                        ? theme.palette.primary.brandSecondary
                        : theme.palette.gray.light,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: isCurrent
                          ? theme.palette.primary.white
                          : "text.secondary",
                      }}
                    >
                      {instruction.id}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      color: isCompleted
                        ? theme.palette.gray.medium
                        : isCurrent
                        ? theme.palette.primary.brandSecondary
                        : theme.palette.primary.brandSecondary,
                      opacity: isCompleted ? 0.5 : 1,
                    }}
                  >
                    {instruction.icon}
                  </Box>
                </Box>

                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    fontWeight: 600,
                    color: isCompleted
                      ? "text.secondary"
                      : isCurrent
                      ? theme.palette.primary.main
                      : "text.primary",
                  }}
                >
                  {instruction.title}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    lineHeight: 1.6,
                    mb: 2,
                    flexGrow: 1,
                  }}
                >
                  {instruction.description}
                  {instruction.code && (
                    <>
                      {" "}
                      <Box
                        component="code"
                        sx={{
                          px: 1,
                          py: 0.5,
                          borderRadius: 0.5,
                          backgroundColor: theme.palette.gray.light,
                          fontSize: "0.875rem",
                          color: "text.primary",
                        }}
                      >
                        {instruction.code}
                      </Box>
                      .
                    </>
                  )}
                </Typography>

                {instruction.details && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      display: "block",
                      mb: 2,
                    }}
                  >
                    {instruction.details}
                  </Typography>
                )}

                {instruction.outputs && (
                  <Box sx={{ mt: "auto" }}>
                    {instruction.outputs.map((output, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          p: 1.5,
                          mb: 1,
                          borderRadius: 1,
                          backgroundColor: theme.palette.gray.lighter,
                          border: "1px solid",
                          borderColor: theme.palette.gray.light,
                        }}
                      >
                        <Box
                          component="code"
                          sx={{
                            minWidth: "fit-content",
                            px: 1,
                            py: 0.5,
                            mr: 1.5,
                            borderRadius: 0.5,
                            backgroundColor: "background.paper",
                            border: "1px solid",
                            borderColor: theme.palette.gray.medium,
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            color: "text.primary",
                          }}
                        >
                          {output.label}
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            lineHeight: 1.4,
                            fontSize: "0.75rem",
                          }}
                        >
                          {output.desc}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                <Box
                  className="step-accent"
                  sx={{
                    position: "absolute",
                    left: 0,
                    top: 16,
                    bottom: 16,
                    width: 3,
                    backgroundColor: isCompleted
                      ? theme.palette.gray.medium
                      : isCurrent
                      ? theme.palette.primary.brandSecondary
                      : theme.palette.gray.medium,
                    borderRadius: "0 2px 2px 0",
                    transition: "background-color 0.3s ease",
                  }}
                />
              </CardContent>
            </Card>
          );
        })}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mt: 3,
          gap: 2,
        }}
      >
        {[1, 2, 3].map((step, index) => {
          const isStepCompleted = completedSteps.has(step);
          const isStepCurrent = currentStep === step;

          return (
            <Box key={step} sx={{ display: "flex", alignItems: "center" }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: isStepCompleted
                    ? theme.palette.gray.medium
                    : isStepCurrent
                    ? theme.palette.primary.brandSecondary
                    : theme.palette.gray.light,
                  transition: "background-color 0.3s ease",
                }}
              />
              {index < 2 && (
                <Box
                  sx={{
                    width: 40,
                    height: 1,
                    backgroundColor:
                      isStepCompleted || currentStep > step + 1
                        ? theme.palette.gray.medium
                        : theme.palette.gray.light,
                    ml: 1,
                    transition: "background-color 0.3s ease",
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
